import {
  ColumnValidationResult,
  parseCsvAndValidateColumns,
} from 'src/utils/CommonHelpers/sharedCSVUtils';
import {
  IndividualSuiteParsedFromCSV,
  ProcessedSuite,
} from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/BulkSaveSuiteAPITypes';
import { useCallback, useState } from 'react';
import { transformSuiteDataFromCSV } from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/Helpers/helpers';
import { toast } from 'react-toastify';
import {
  addBulkSuites,
  getItems,
} from 'src/services/client';
import {
  CatalogueItem,
  CatalogueMismatchError,
  ClassifiedValidationResult,
  classifyValidationErrors,
  collectUniqueCsvAssetNames,
  extractOptionalMissingColumns,
  extractPresentOptionalColumns,
  ParkingPermissionCheckResult,
  RawRowValidationError,
  UnitRowValidationError,
  validateCatalogueAssets,
  validateParkingStoragePermission,
  validateProcessedSuites,
  validateRawUnitRows,
} from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/Helpers/rowValidationHelpers';
import { MANDATORY_UNIT_COLUMNS } from 'src/pages/Suite/Components/UnitImportDialogue/Hooks/Helpers/config';
import { useSelector } from 'react-redux';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  FetchAllowedTabListResult,
  usePropertiesAPI,
} from 'src/SharedCustomHooks/UsePropertiesAPI/usePropertiesAPI';

type ValidationStatus = 'idle' | 'validating' | 'valid' | 'invalid';
type ImportStatus = 'idle' | 'importing' | 'success' | 'error';

interface ImportResult {
  totalRecords: number;
}

export interface UseUnitImportReturn {
  file: File | null;
  validationStatus: ValidationStatus;
  columnValidation: ColumnValidationResult;
  optionalMissingColumns: string[];
  parsedRecords: ProcessedSuite[];
  rowValidationErrors: UnitRowValidationError[];
  rowValidationWarnings: UnitRowValidationError[];
  catalogueMismatch: CatalogueMismatchError | null;
  parkingPermissionError: ParkingPermissionCheckResult | null;
  importStatus: ImportStatus;
  importResult: ImportResult | null;
  apiError: string | null;
  processFile: (file: File) => void;
  runImport: () => Promise<void>;
  reset: () => void;
}

export const useUnitImport = (
  propertyId: string,
  userId: string
): UseUnitImportReturn => {
  const user = useSelector(selectAuthenticatedUser);

  const { fetchAllowedTabList } = usePropertiesAPI();

  const [file, setFile] = useState<File | null>(null);
  const [validationStatus, setValidationStatus] =
    useState<ValidationStatus>('idle');
  const [columnValidation, setColumnValidation] =
    useState<ColumnValidationResult>({ found: [], missing: [] });
  const [optionalMissingColumns, setOptionalMissingColumns] = useState<
    string[]
  >([]);
  const [parsedRecords, setParsedRecords] = useState<ProcessedSuite[]>([]);
  const [rowValidationErrors, setRowValidationErrors] = useState<
    UnitRowValidationError[]
  >([]);
  const [rowValidationWarnings, setRowValidationWarnings] = useState<
    UnitRowValidationError[]
  >([]);
  const [catalogueMismatch, setCatalogueMismatch] =
    useState<CatalogueMismatchError | null>(null);
  const [parkingPermissionError, setParkingPermissionError] =
    useState<ParkingPermissionCheckResult | null>(null);
  const [importStatus, setImportStatus] = useState<ImportStatus>('idle');
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  const processFile = async (selectedFile: File): Promise<void> => {
    setFile(selectedFile);
    setValidationStatus('validating');
    setApiError(null);
    setCatalogueMismatch(null);
    setParkingPermissionError(null);
    setOptionalMissingColumns([]);

    let catalogueItems: CatalogueItem[] = [];

    try {
      const resItems: any = await getItems(user.id, user.roles[0].role.name);

      const resItemsData = await resItems.json();

      const catalogueItemsFromGoodEndpoint = (resItemsData ?? []).map(
        (item: any) => ({
          id: item.id,
          name: item.name,
        })
      );

      catalogueItems = catalogueItemsFromGoodEndpoint;
    } catch (error) {
      console.error('[useUnitImport] Failed to fetch item types:', error);
    }

    const allowedTabListResult: FetchAllowedTabListResult =
      await fetchAllowedTabList(propertyId);

    const reader = new FileReader();

    reader.onload = (e: ProgressEvent<FileReader>) => {
      const fileText = e.target?.result as string;

      const {
        data,
        columnValidation: colValidation,
        isValid,
      } = parseCsvAndValidateColumns(fileText, MANDATORY_UNIT_COLUMNS);

      setColumnValidation(colValidation);

      if (!isValid) {
        setValidationStatus('invalid');
        setParsedRecords([]);
        setRowValidationErrors([]);
        return;
      }

      const missingOptional: string[] = extractOptionalMissingColumns(fileText);
      const presentOptional: string[] = extractPresentOptionalColumns(fileText);

      setOptionalMissingColumns(missingOptional);

      const nonEmptyRows: IndividualSuiteParsedFromCSV[] = data.filter(
        (row: any) => row.Number && row.Number.trim() !== ''
      );

      if (!allowedTabListResult.ok) {
        console.warn(
          '[useUnitImport] Could not verify Parking Details permission — blocking parking/storage rows defensively'
        );
      }

      const parkingStallsPermissionCheck: ParkingPermissionCheckResult =
        validateParkingStoragePermission(
          nonEmptyRows,
          allowedTabListResult.allowedTabList
        );

      if (parkingStallsPermissionCheck.hasPermissionError) {
        setParkingPermissionError(parkingStallsPermissionCheck);
        setValidationStatus('invalid');
        return;
      }

      // Stage 1: raw row structural validation.
      const rawErrors: RawRowValidationError[] =
        validateRawUnitRows(nonEmptyRows);

      if (rawErrors.length > 0) {
        setRowValidationErrors(rawErrors);
        setValidationStatus('invalid');
        return;
      }

      const transformedSuites: ProcessedSuite[] =
        transformSuiteDataFromCSV(nonEmptyRows);

      // Stage 2: validate fields, then classify into blocking errors vs warnings.
      const postErrors: UnitRowValidationError[] =
        validateProcessedSuites(transformedSuites);

      const classified: ClassifiedValidationResult =
        classifyValidationErrors(postErrors);

      setParsedRecords(transformedSuites);
      setRowValidationErrors(classified.errors);
      setRowValidationWarnings(classified.warnings);

      if (classified.errors.length > 0) {
        setValidationStatus('invalid');
        return;
      }

      // Stage 3: Catalogue asset validation.
      const hasAssetColumns: boolean =
        presentOptional.some((col: string) => col.toLowerCase() === 'rooms') &&
        presentOptional.some((col: string) => col.toLowerCase() === 'assets');

      if (!hasAssetColumns) {
        setValidationStatus('valid');
        return;
      }

      if (catalogueItems.length === 0) {
        const allCsvAssetNames: string[] =
          collectUniqueCsvAssetNames(transformedSuites);

        setCatalogueMismatch({
          unmatchedCsvAssets: allCsvAssetNames,
          catalogueItemNames: [],
        });

        setValidationStatus('invalid');
        return;
      }

      const mismatch: CatalogueMismatchError | null = validateCatalogueAssets(
        transformedSuites,
        catalogueItems
      );

      if (mismatch) {
        setCatalogueMismatch(mismatch);
        setValidationStatus('invalid');
        return;
      }

      setValidationStatus('valid');
    };

    reader.onerror = () => {
      setValidationStatus('invalid');
      setColumnValidation({ found: [], missing: MANDATORY_UNIT_COLUMNS });
    };

    reader.readAsText(selectedFile);
  };

  const runImport = async (): Promise<void> => {
    if (!parsedRecords.length) {
      return;
    }

    try {
      setImportStatus('importing');
      setApiError(null);

      const response: any = await addBulkSuites({
        propertyId,
        userId,
        suiteList: parsedRecords,
      });

      const responseJson = await response.json();

      if (response.status !== 200) {
        const errorMessage = responseJson?.message || 'Failed to import units';
        setApiError(errorMessage);
        setImportStatus('error');

        toast.error('🚫 Failed to import units, please try again.', {
          autoClose: 12000,
          position: 'top-center',
        });

        return;
      }

      setImportResult({ totalRecords: parsedRecords.length });
      setImportStatus('success');

      toast.success(
        `✅ ${parsedRecords.length} unit(s) imported successfully!`,
        {
          autoClose: 8000,
          position: 'top-center',
        }
      );
    } catch (error) {
      console.error('Unit import error:', error);
      const errorMessage = 'Something went wrong — please try again.';
      setApiError(errorMessage);
      setImportStatus('error');

      toast.error(`🚫 ${errorMessage}`, {
        autoClose: 12000,
        position: 'top-center',
      });
    }
  };

  const reset = useCallback((): void => {
    setFile(null);
    setValidationStatus('idle');
    setColumnValidation({ found: [], missing: [] });
    setParsedRecords([]);
    setRowValidationErrors([]);
    setCatalogueMismatch(null);
    setParkingPermissionError(null);
    setImportStatus('idle');
    setImportResult(null);
    setApiError(null);
  }, []);

  return {
    file,
    validationStatus,
    columnValidation,
    optionalMissingColumns,
    parsedRecords,
    rowValidationErrors,
    rowValidationWarnings,
    catalogueMismatch,
    parkingPermissionError,
    importStatus,
    importResult,
    apiError,
    processFile,
    runImport,
    reset,
  };
};
